import type { FileService, FileHandle, SelectFilesOptions, WriteOptions } from '@exiftrator/shared';

export function createFileService(): FileService {
  return {
    async readFile(file: File | string): Promise<Uint8Array> {
      if (typeof file === 'string') {
        throw new Error('Web platform does not support reading files by path');
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          resolve(new Uint8Array(arrayBuffer));
        };
        reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
        reader.readAsArrayBuffer(file);
      });
    },

    async writeFile(buffer: Uint8Array, filename: string, options?: WriteOptions): Promise<boolean> {
      try {
        const arrayBuffer = buffer.buffer.slice(
          buffer.byteOffset,
          buffer.byteOffset + buffer.byteLength,
        ) as ArrayBuffer;
        const blob = new Blob([arrayBuffer], { type: options?.mimeType || 'image/jpeg' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';

        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return true;
      } catch (error) {
        console.error('Download failed:', error);
        return false;
      }
    },

    async selectFiles(options?: SelectFilesOptions): Promise<FileHandle[]> {
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = options?.multiple !== false;
        input.accept = options?.accept || '.jpg,.jpeg,image/jpeg';
        input.style.display = 'none';

        input.onchange = (e) => {
          const files = Array.from((e.target as HTMLInputElement).files || []);
          document.body.removeChild(input);

          resolve(files.map(file => ({
            name: file.name,
            path: undefined,
            size: file.size,
            type: file.type,
            file,
            lastModified: file.lastModified,
          })));
        };

        input.oncancel = () => {
          document.body.removeChild(input);
          resolve([]);
        };

        document.body.appendChild(input);
        input.click();
      });
    },

    createObjectURL(file: File | Blob): string {
      return URL.createObjectURL(file);
    },

    revokeObjectURL(url: string): void {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    },
  };
}
